import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import {
  Box, Typography, Button, Chip, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,
  Alert, CircularProgress, Divider, Paper, Grid,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  PlayArrow as StartIcon,
  Assignment as InspectIcon,
  CheckCircle as DoneIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { api, apiRoutes } from '../../api';
import InspectionWizard from './InspectionWizard';

// Lazy-load AgentCalendar so a missing FullCalendar install doesn't crash the whole panel
const AgentCalendar = lazy(() =>
  import('./AgentCalendar').catch(() => ({
    default: () => (
      <Alert severity="warning">
        تقویم در دسترس نیست. لطفاً FullCalendar را نصب کنید:
        <br />
        <code>npm install @fullcalendar/react @fullcalendar/core @fullcalendar/daygrid @fullcalendar/timegrid @fullcalendar/interaction</code>
      </Alert>
    ),
  }))
);

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

// Maps task status to stepper index
const STATUS_STEP = {
  PENDING: 0, CLAIMABLE: 0, ASSIGNED: 1,
  SCHEDULED: 2, IN_PROGRESS: 3, COMPLETED: 4, CANCELLED: 4,
};

const STATUS_LABEL = {
  PENDING: 'در انتظار', CLAIMABLE: 'قابل دریافت', ASSIGNED: 'دریافت‌شده',
  SCHEDULED: 'زمان‌بندی‌شده', IN_PROGRESS: 'در جریان',
  COMPLETED: 'تکمیل‌شده', CANCELLED: 'لغوشده',
};

const STATUS_COLOR = {
  CLAIMABLE: 'warning', ASSIGNED: 'info', SCHEDULED: 'primary',
  IN_PROGRESS: 'secondary', COMPLETED: 'success', CANCELLED: 'error', PENDING: 'default',
};

// ── Property info card shown at the top of the task detail ───────────────────
function PropertyInfoCard({ property }) {
  if (!property) return null;
  const rows = [
    { label: 'آدرس', value: property.address },
    { label: 'محله', value: property.neighborhood_name },
    { label: 'شهر', value: property.city_name },
    { label: 'کد پستی', value: property.postal_code },
    { label: 'متراژ', value: property.area ? `${property.area} متر مربع` : null },
    { label: 'سال ساخت', value: property.establish_date },
    { label: 'نوع آگهی', value: property.listing_type },
  ].filter(r => r.value);

  const owner = property.owner;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>اطلاعات ملک</Typography>
      <Grid container spacing={1}>
        {rows.map(r => (
          <Grid item xs={12} sm={6} key={r.label}>
            <Typography variant="caption" color="text.secondary">{r.label}</Typography>
            <Typography variant="body2">{r.value}</Typography>
          </Grid>
        ))}
        {property.latitude && property.longitude && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">موقعیت جغرافیایی</Typography>
            <Typography variant="body2" dir="ltr">
              {property.latitude.toFixed(6)}, {property.longitude.toFixed(6)}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
              href={`https://maps.google.com/?q=${property.latitude},${property.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              نمایش در نقشه
            </Button>
          </Grid>
        )}
        {property.description && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">توضیحات</Typography>
            <Typography variant="body2">{property.description}</Typography>
          </Grid>
        )}
      </Grid>

      {owner && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle1" gutterBottom>اطلاعات مالک</Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">نام</Typography>
              <Typography variant="body2">{owner.full_name || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">شماره موبایل</Typography>
              <Typography variant="body2" dir="ltr">{owner.phone || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">کد ملی</Typography>
              <Typography variant="body2" dir="ltr">{owner.national_id || '—'}</Typography>
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
}

// ── Visit scheduling dialog — uses the full AgentCalendar in visit mode ──────
function ScheduleVisitDialog({ open, taskId, taskLabel, onClose, onScheduled }) {
  const [notes, setNotes] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [visitingWindows, setVisitingWindows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [windowsLoading, setWindowsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch visiting windows when dialog opens
  useEffect(() => {
    if (!open || !taskId) return;
    setWindowsLoading(true);
    api.get(apiRoutes.inspectionTasks.visitingWindows(taskId), { headers: authHeader() })
      .then(res => setVisitingWindows(res.data || []))
      .catch(() => setVisitingWindows([]))
      .finally(() => setWindowsLoading(false));
  }, [open, taskId]);

  const handleVisitSelected = (start, end) => {
    setSelectedSlot({ start, end });
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setLoading(true); setError(null);
    try {
      await api.post(
        apiRoutes.inspectionTasks.visits(taskId),
        { start: selectedSlot.start, end: selectedSlot.end, notes },
        { headers: authHeader() }
      );
      onScheduled();
      onClose();
      setSelectedSlot(null);
      setNotes('');
    } catch (err) {
      setError(err.response?.data?.detail || 'خطا در زمان‌بندی بازدید');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">انتخاب زمان بازدید — {taskLabel}</Typography>
          <Button startIcon={<CloseIcon />} onClick={onClose} color="inherit" size="small">بستن</Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto', pb: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {visitingWindows.length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            زمان‌های سبز رنگ توسط مالک مجاز اعلام شده‌اند. فقط در این بازه‌ها می‌توانید بازدید تنظیم کنید.
          </Alert>
        )}
        {visitingWindows.length === 0 && !windowsLoading && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            مالک هنوز بازه زمانی مجاز تعریف نکرده است. می‌توانید هر زمانی انتخاب کنید.
          </Alert>
        )}
        {windowsLoading
          ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          : (
            <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
              <AgentCalendar
                visitMode
                taskLabel={taskLabel}
                visitingWindows={visitingWindows}
                onVisitSelected={handleVisitSelected}
              />
            </Suspense>
          )
        }
        {selectedSlot && (
          <Box mt={2}>
            <TextField
              label="یادداشت بازدید (اختیاری)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              fullWidth size="small" multiline rows={2}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>لغو</Button>
        <Button variant="contained" color="success" disabled={!selectedSlot || loading}
          onClick={handleConfirm}>
          {loading ? <CircularProgress size={18} /> : 'ثبت بازدید'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main TaskDetailPanel ──────────────────────────────────────────────────────
export default function TaskDetailPanel({ task, onBack, onTaskUpdated }) {
  const [currentTask, setCurrentTask] = useState(task);
  const [visits, setVisits] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInspectionForm, setShowInspectionForm] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await api.get(apiRoutes.inspectionTasks.detail(task.id), { headers: authHeader() });
      setCurrentTask(res.data);
      onTaskUpdated?.();
    } catch (e) {  }
  }, [task.id, onTaskUpdated]);

  const loadVisits = useCallback(async () => {
    try {
      const res = await api.get(apiRoutes.inspectionTasks.visits(task.id), { headers: authHeader() });
      setVisits(res.data.results || res.data || []);
    } catch (e) {  }
  }, [task.id]);

  useEffect(() => { loadVisits(); }, [loadVisits]);

  const handleStart = async () => {
    setActionLoading(true); setError(null);
    try {
      await api.post(apiRoutes.inspectionTasks.start(task.id), {}, { headers: authHeader() });
      await reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'خطا در شروع بازرسی');
    } finally {
      setActionLoading(false);
    }
  };

  const s = currentTask.status;
  const activeStep = STATUS_STEP[s] ?? 0;

  return (
    <Box dir="rtl" className="agents-section agents-task-detail">
      <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mb: 2 }}>بازگشت به لیست</Button>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6">{currentTask.property?.address || 'بازرسی ملک'}</Typography>
          <Typography variant="body2" color="text.secondary">{currentTask.property?.neighborhood?.name}</Typography>
        </Box>
        <Chip label={STATUS_LABEL[s] || s} color={STATUS_COLOR[s] || 'default'} />
      </Box>

      {/* Progress stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {['دریافت وظیفه', 'زمان‌بندی بازدید', 'بازدید', 'بازرسی', 'تکمیل'].map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Divider sx={{ mb: 3 }} />

      {/* Property + owner info */}
      <PropertyInfoCard property={currentTask.property} />

      {/* Action buttons based on status */}
      {s === 'ASSIGNED' && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>مرحله بعد: زمان‌بندی بازدید</Typography>
          <Button variant="contained" startIcon={<CalendarIcon />} onClick={() => setScheduleOpen(true)}>
            زمان‌بندی بازدید
          </Button>
        </Box>
      )}

      {s === 'SCHEDULED' && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>بازدید زمان‌بندی شده — وقتی رسیدید شروع کنید</Typography>
          <Button variant="contained" color="warning" startIcon={<StartIcon />}
            onClick={handleStart} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : 'شروع بازرسی (در محل هستم)'}
          </Button>
        </Box>
      )}

      {s === 'IN_PROGRESS' && !showInspectionForm && (
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>بازرسی در جریان است — فرم را تکمیل کنید</Typography>
          <Button variant="contained" color="primary" startIcon={<InspectIcon />}
            onClick={() => setShowInspectionForm(true)}>
            تکمیل فرم بازرسی
          </Button>
        </Box>
      )}

      {s === 'IN_PROGRESS' && showInspectionForm && (
        <InspectionWizard
          taskId={task.id}
          property={currentTask.property}
          onSubmitted={async () => { await reload(); setShowInspectionForm(false); }}
        />
      )}

      {s === 'COMPLETED' && (
        <Alert severity="success" icon={<DoneIcon />}>
          بازرسی این ملک با موفقیت تکمیل شده است.
        </Alert>
      )}

      {/* Scheduled visits list */}
      {visits.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>بازدیدهای زمان‌بندی‌شده</Typography>
          {visits.map(v => (
            <Paper key={v.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography variant="body2">
                {new Date(v.start_at || v.start).toLocaleString('fa-IR')} — {new Date(v.end_at || v.end).toLocaleString('fa-IR')}
              </Typography>
              {v.meta?.notes && <Typography variant="caption" color="text.secondary">{v.meta.notes}</Typography>}
            </Paper>
          ))}
        </Box>
      )}

      <ScheduleVisitDialog
        open={scheduleOpen}
        taskId={task.id}
        taskLabel={currentTask.property?.address || 'بازرسی'}
        onClose={() => setScheduleOpen(false)}
        onScheduled={async () => { await reload(); await loadVisits(); }}
      />
    </Box>
  );
}
